import { useQuery } from "@tanstack/react-query"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"

import type { FlowChartDataPoint } from "../types/flowChartTypes.ts"

export default function FlowChart({
  fetcher,
  location,
}: {
  fetcher: () => Promise<FlowChartDataPoint[]>
  location: number
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["flowChart", { location }],
    queryFn: fetcher,
    refetchInterval: 60000,
  })

  if (isPending)
    return (
      <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden">
        <h2 className="text-lg font-semibold">24 Hour Flow Trend</h2>
        <Skeleton className="min-h-[425px] w-full mt-[10px]" />
      </div>
    )

  if (isError)
    return (
      <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden">
        <h2 className="text-lg font-semibold">24 Hour Flow Trend</h2>
        <div className="text-status-bad min-h-[425px] w-full mt-[10px] font-semibold">
          Network Error
        </div>
      </div>
    )

  const chartConfig = {
    inlet: {
      label: "Inlet",
      color: "var(--chart-1)",
    },
    methane: {
      label: "Inlet Methane",
      color: "var(--chart-2)",
    },
    outlet: {
      label: "Outlet",
      color: "var(--chart-3)",
    },
  } satisfies ChartConfig

  return (
    <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden bg-card">
      <h2 className="text-lg font-semibold">24 Hour Flow Trend</h2>
      <Label className="text-muted-foreground text-[12pt]">
        <i>(SCFM)</i>
      </Label>
      <ChartContainer className="min-h-[300px] w-full max-h-[450px]" config={chartConfig}>
        <AreaChart data={data} margin={{ top: 25, left: -20, right: 10 }}>
          <defs>
            <linearGradient id="fillInlet" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-inlet)" stopOpacity={0.8} />
              <stop offset="65%" stopColor="var(--color-inlet)" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="fillOutlet" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-outlet)" stopOpacity={0.8} />
              <stop offset="65%" stopColor="var(--color-outlet)" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <XAxis dataKey="timestamp" minTickGap={10} tickLine={false} />
          <YAxis tickLine={false} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={<ChartLegendContent />} />
          <CartesianGrid vertical={false} />
          <Area
            type="monotone"
            dataKey="inlet"
            stroke="var(--color-inlet)"
            fill="var(--color-inlet)"
            strokeWidth={1}
            fillOpacity={0.2}
          />
          <Area
            type="monotone"
            dataKey="methane"
            stroke="var(--color-methane)"
            fill="var(--color-methane)"
            strokeWidth={1}
            fillOpacity={0.2}
          />
          <Area
            dot={false}
            type="monotone"
            dataKey="outlet"
            stroke="var(--color-outlet)"
            fill="var(--color-outlet)"
            fillOpacity={0.2}
            strokeWidth={1}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}
